"use client"

/**
 * Analytics + error helpers for the insurance coverage checker.
 *
 * Fires through the shared `posthog` instance (`lib/posthog.ts`), which gates
 * non-production events centrally, AND mirrors every event to the GTM
 * dataLayer. There is no automatic PostHog→GTM bridge on this site (see
 * `lib/gtm.ts`), so the mirror has to be explicit — the same dataLayer push
 * the checker did as a standalone embed. Keep the `insurance_form_*` /
 * `proforma_*` event names stable: GA4 conversion tags key off them.
 */

import * as Sentry from "@sentry/nextjs"
import { posthog } from "@/lib/posthog"
import { pushDataLayer } from "@/lib/gtm"
import { stripPiiForPostHog } from "@ahead-health/core/analytics/posthogPii"

type EventProperties = Record<string, unknown>

export function trackEvent(
  name: string,
  properties: EventProperties = {}
): void {
  if (typeof window === "undefined") return
  // PostHog gets the payload with identity fields stripped (AHD-9528). This is
  // the chokepoint the leak went through: `insurance_form_submitted` carried a
  // raw customer `email` on 100% of gate submissions.
  posthog.capture(name, stripPiiForPostHog(properties))
  // GTM dataLayer mirror (gated by NEXT_PUBLIC_ANALYTICS_ENABLED inside
  // pushDataLayer). Restores the dataLayer events GA4 conversions key off,
  // which were dropped when the checker was ported out of its own repo.
  // Deliberately gets the FULL payload — GA4 conversion tags and Meta
  // advanced matching read these fields here, and Meta hashes them itself.
  // Never strip this one.
  pushDataLayer({ event: name, ...properties })
}

/**
 * The visitor's current PostHog `distinct_id`. Sent with the gate / proforma
 * POSTs so the server can merge this anonymous browser person into the
 * email-hash person (capture-time email→app identity stitch). Returns "" when
 * PostHog hasn't resolved an id yet — the server then skips the stitch.
 */
export function getPostHogDistinctId(): string {
  if (typeof window === "undefined") return ""
  return posthog.get_distinct_id?.() ?? ""
}

// ── Internal / test traffic flag (AHD-10005) ────────────────────────────────
//
// The salted email hash used for lead→booking joins is computed SERVER-SIDE
// (email-flows `insurance_lead`, AHD-10004) — the salt is a secret and must
// never ship in this bundle. What the client CAN safely compute is whether the
// address is internal/test traffic, so insights can exclude it without ever
// seeing the raw address. Same regexes as ahead-booking-new (AHD-9964).

const INTERNAL_EMAIL_DOMAIN =
  /@([a-z0-9-]+\.)*(aheadhealth\.com|ahead\.clinic|example\.com|testmail\.app)$/i

// "test" as a whole token in the local part (test@, test1@, john.test@,
// test+tag@) — NOT as a substring, which would permanently mislabel real
// customers like attest@… / protest.…@…. Queries can be loosened later; a
// stored flag cannot.
const TEST_LOCAL_PART = /(^|[._+-])test($|[._+\d-])/

export function isInternalEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase()
  if (INTERNAL_EMAIL_DOMAIN.test(normalized)) return true
  const at = normalized.indexOf("@")
  return at > 0 && TEST_LOCAL_PART.test(normalized.slice(0, at))
}

export function captureException(
  err: unknown,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(err, context ? { extra: context } : undefined)
}

/**
 * Network-level `fetch()` failures (offline, ad blocker, DNS, dropped
 * connection, user navigated away mid-request) — per-user and unactionable, so
 * not worth reporting to Sentry.
 *
 * Matched by message signature rather than `instanceof TypeError`: a rejection
 * raised in a different JS realm or rewrapped by third-party fetch
 * instrumentation (Sentry/PostHog) has a constructor from another realm, so
 * `instanceof` returns false. The `.cause` chain is unwrapped since the network
 * failure is sometimes nested inside an application-level wrapper.
 */
export function isFetchNetworkError(err: unknown): boolean {
  const messages: string[] = []
  const collect = (value: unknown): void => {
    if (
      typeof value === "object" &&
      value !== null &&
      typeof (value as { message?: unknown }).message === "string"
    ) {
      messages.push((value as { message: string }).message)
      const cause = (value as { cause?: unknown }).cause
      if (cause) collect(cause)
    }
  }
  collect(err)
  return messages.some(
    (msg) =>
      msg === "Load failed" || // Safari
      msg === "Failed to fetch" || // Chrome / Edge
      msg === "cancelled" || // Safari mid-request abort
      msg.startsWith("NetworkError") // Firefox
  )
}
